/**
 * Hladiny významnosti, intervaly spolehlivosti a varování u malých vzorků.
 *
 * ZRCADLÍ backend core/stats.py — `significance_label()`, `wilson_ci()` a
 * `small_sample_warnings()`. Backend většinu z toho posílá hotové
 * (`significance_cs`, `warnings_cs`, `ProportionOut.ci_low/ci_high`); tyhle
 * funkce jsou pro místa, kde UI dostane holá čísla — typicky časová řada, kde
 * se CI pás musí dopočítat z `clicks`/`impressions`.
 */
import {
  SMALL_SAMPLE_TRIALS,
  SMALL_SAMPLE_SUCCESSES,
  type Comparison,
  type ExperimentComparison,
  type GuardComparison,
} from '@/api/models';

/**
 * Moduly overview / guard / experimenty mají v OpenAPI vlastní `ComparisonOut`
 * s drobně jinými názvy polí (`warnings` vs `warnings_cs`, `diff_pb` vs
 * `diff_pb_text`, `significance_label_cs` vs `significance_cs`). Význam je
 * stejný, takže UI je sjednotí sem místo aby mělo tři varianty badge.
 */
export type AnyComparison = Comparison | GuardComparison | ExperimentComparison;

/** Sjednocený tvar, se kterým pracují komponenty. */
export interface NormalizedComparison {
  labelA: string;
  labelB: string;
  diff: number;
  diffText: string;
  diffRelative: number | null;
  pValue: number;
  significant: boolean;
  significanceText: string;
  winner: 'a' | 'b' | 'tie';
  nTotal: number;
  warnings: string[];
  a: { successes: number; trials: number } | null;
  b: { successes: number; trials: number } | null;
}

function side(value: unknown): { successes: number; trials: number } | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const successes = record.successes;
  const trials = record.trials;
  if (typeof successes !== 'number' || typeof trials !== 'number') return null;
  return { successes, trials };
}

export function normalizeComparison(
  comparison: AnyComparison | null | undefined,
): NormalizedComparison | null {
  if (!comparison) return null;
  const c = comparison as Record<string, unknown>;
  return {
    labelA: typeof c.label_a === 'string' ? c.label_a : 'A',
    labelB: typeof c.label_b === 'string' ? c.label_b : 'B',
    diff: typeof c.diff === 'number' ? c.diff : 0,
    diffText:
      (typeof c.diff_pb_text === 'string' && c.diff_pb_text) ||
      (typeof c.diff_pb === 'string' && c.diff_pb) ||
      '',
    diffRelative: typeof c.diff_relative === 'number' ? c.diff_relative : null,
    pValue: typeof c.p_value === 'number' ? c.p_value : Number.NaN,
    significant: Boolean(c.significant),
    significanceText:
      (typeof c.significance_cs === 'string' && c.significance_cs) ||
      (typeof c.significance_label_cs === 'string' && c.significance_label_cs) ||
      '',
    winner: c.winner === 'a' || c.winner === 'b' ? c.winner : 'tie',
    nTotal: typeof c.n_total === 'number' ? c.n_total : 0,
    warnings: Array.isArray(c.warnings_cs)
      ? (c.warnings_cs as string[])
      : Array.isArray(c.warnings)
        ? (c.warnings as string[])
        : [],
    a: side(c.a),
    b: side(c.b),
  };
}

export type SignificanceLevel =
  | 'highly-significant'
  | 'significant-01'
  | 'significant-05'
  | 'borderline'
  | 'not-significant';

export interface SignificanceInfo {
  level: SignificanceLevel;
  /** česká hláška shodná se significance_label() na backendu */
  label: string;
  /** krátký štítek do tabulek */
  short: string;
  /** má se brát jako významné (p < 0,05) */
  isSignificant: boolean;
}

/**
 * Pět hladin přesně podle significance_label() z core/stats.py:
 *   p < 0,001  → vysoce významné
 *   p < 0,01   → významné (p<0,01)
 *   p < 0,05   → významné (p<0,05)
 *   p < 0,1    → hraniční
 *   jinak      → nevýznamné
 *
 * Hranice jsou OSTRÉ (`<`, ne `<=`) — p = 0,05 je hraniční, ne významné.
 */
export function significanceOf(pValue: number | null | undefined): SignificanceInfo {
  if (pValue === null || pValue === undefined || !Number.isFinite(pValue)) {
    return {
      level: 'not-significant',
      label: 'nevýznamné',
      short: 'n. v.',
      isSignificant: false,
    };
  }
  if (pValue < 0.001) {
    return {
      level: 'highly-significant',
      label: 'vysoce významné (p<0,001)',
      short: 'p<0,001',
      isSignificant: true,
    };
  }
  if (pValue < 0.01) {
    return {
      level: 'significant-01',
      label: 'významné (p<0,01)',
      short: 'p<0,01',
      isSignificant: true,
    };
  }
  if (pValue < 0.05) {
    return {
      level: 'significant-05',
      label: 'významné (p<0,05)',
      short: 'p<0,05',
      isSignificant: true,
    };
  }
  if (pValue < 0.1) {
    return {
      level: 'borderline',
      label: 'hraniční (p<0,1)',
      short: 'hraniční',
      isSignificant: false,
    };
  }
  return {
    level: 'not-significant',
    label: 'nevýznamné',
    short: 'n. v.',
    isSignificant: false,
  };
}

/** z pro 95% interval — shodné se Z_95 v core/stats.py. */
const Z_95 = 1.959963984540054;

/**
 * Wilsonův skórový interval — zrcadlí wilson_ci() z core/stats.py.
 * Na rozdíl od normální aproximace se chová rozumně i u malých n a u měr
 * blízkých 0 nebo 1, což je přesně případ CTR kolem 4 %.
 */
export function wilsonCi(
  successes: number,
  trials: number,
  z: number = Z_95,
): [number, number] {
  if (trials <= 0) return [0, 0];
  const p = successes / trials;
  const denom = 1 + (z * z) / trials;
  const centre = p + (z * z) / (2 * trials);
  const margin = z * Math.sqrt((p * (1 - p)) / trials + (z * z) / (4 * trials * trials));
  return [(centre - margin) / denom, (centre + margin) / denom];
}

export interface SmallSampleCheck {
  isSmall: boolean;
  warnings: string[];
}

/**
 * Varování u malých vzorků. Zrcadlí small_sample_warnings() z core/stats.py:
 * pod 1 000 pokusů nebo pod 30 úspěchů v kterékoli větvi.
 */
export function checkSmallSample(
  successes: number | null | undefined,
  trials: number | null | undefined,
  label = 'Vzorek',
): SmallSampleCheck {
  const warnings: string[] = [];
  const s = successes ?? 0;
  const t = trials ?? 0;

  if (t < SMALL_SAMPLE_TRIALS) {
    warnings.push(
      `${label}: malý vzorek (${t.toLocaleString('cs-CZ')} impresí < ${SMALL_SAMPLE_TRIALS.toLocaleString('cs-CZ')}) — výsledek berte jako orientační.`,
    );
  }
  if (s < SMALL_SAMPLE_SUCCESSES) {
    warnings.push(
      `${label}: jen ${s.toLocaleString('cs-CZ')} kliků — normální aproximace je nespolehlivá, preferujte Bayesovské vyhodnocení.`,
    );
  }

  return { isSmall: warnings.length > 0, warnings };
}

/**
 * Varování z celého srovnání. Backend je posílá hotová v `warnings_cs`; pokud
 * je neposlal, dopočítáme je ze stejných prahů, ať uživatel varování nikdy
 * neztratí jen kvůli tvaru odpovědi.
 */
export function comparisonWarnings(comparison: AnyComparison | null | undefined): string[] {
  const c = normalizeComparison(comparison);
  if (!c) return [];
  if (c.warnings.length > 0) return c.warnings;
  return [
    ...(c.a ? checkSmallSample(c.a.successes, c.a.trials, c.labelA).warnings : []),
    ...(c.b ? checkSmallSample(c.b.successes, c.b.trials, c.labelB).warnings : []),
  ];
}

/** Vítěz srovnání v češtině. */
export function winnerLabel(comparison: AnyComparison | null | undefined): string {
  const c = normalizeComparison(comparison);
  if (!c) return '—';
  if (c.winner === 'tie') return 'remíza';
  return c.winner === 'a' ? c.labelA : c.labelB;
}
